import { useMutation, useQueryClient } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { CreateReactionData, ReactionResponse } from "src/pages/Social/types/reaction.types";
import { Post } from "src/pages/Social/types/post.types";
import { SOCIAL_FEED_KEY, SOCIAL_POST_KEY, SOCIAL_POSTS_KEY } from "./usePosts";

// Toggle reaction (like/unlike)
export const useToggleReaction = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ postId, type }: CreateReactionData) => {
            const response = await loggedInApiCall.post(`/api/social/posts/${postId}/react`, { type });
            return response.data as ReactionResponse;
        },
        onSuccess: (reactionResponse, { postId }) => {
            // Update single post cache
            const existingPost = queryClient.getQueryData<Post>([SOCIAL_POST_KEY, postId]);
            if (existingPost) {
                queryClient.setQueryData([SOCIAL_POST_KEY, postId], {
                    ...existingPost,
                    reactionsSummary: reactionResponse.reactionSummary,
                    reactionCount: reactionResponse.totalReactions,
                    userReaction: reactionResponse.action === "added" ? reactionResponse.reaction?.type : undefined,
                });
            }

            // Update post in all feeds
            queryClient.setQueriesData<any>([SOCIAL_FEED_KEY], (oldData: any) => {
                if (!oldData) return oldData;

                return {
                    ...oldData,
                    pages: oldData.pages.map((page: any) => ({
                        ...page,
                        posts: page.posts.map((post: Post) => {
                            if (post.id !== postId) return post;

                            return {
                                ...post,
                                reactionsSummary: reactionResponse.reactionSummary,
                                reactionCount: reactionResponse.totalReactions,
                                userReaction:
                                    reactionResponse.action === "added" ? reactionResponse.reaction?.type : undefined,
                            };
                        }),
                    })),
                };
            });

            // Update posts list cache
            queryClient.setQueriesData<any>([SOCIAL_POSTS_KEY], (oldData: any) => {
                if (!oldData) return oldData;

                return {
                    ...oldData,
                    pages: oldData.pages.map((page: any) => ({
                        ...page,
                        posts: page.posts.map((post: Post) => {
                            if (post.id !== postId) return post;

                            return {
                                ...post,
                                reactionsSummary: reactionResponse.reactionSummary,
                                reactionCount: reactionResponse.totalReactions,
                                userReaction:
                                    reactionResponse.action === "added" ? reactionResponse.reaction?.type : undefined,
                            };
                        }),
                    })),
                };
            });

            // Invalidate trending topics cache as reactions affect trending activity
            queryClient.invalidateQueries(["trendingTopics"]);
        },
        onError: (error) => {
            console.error("Failed to toggle reaction:", error);
        },
    });
};
